<template>
  <div class="list">
    <p v-if="label" class="subtitle-2">{{ label }}</p>
    <div class="add-item-button-wrapper">
      <listAddModal
        :key="modalKey"
        v-if="getFieldValue('useModalForAdd')"
        :label="label"
        :availableFieldList="availableFieldList"
        :category="category"
        :ruleId="ruleId"
        :editValues="editValues"
        :isTemplate="isTemplate"
        @addItem="addItem"
        @clearEditValues="clearEditValues"
      />
      <qvoButton
        v-else
        outlined
        @click="addItem({})"
        leftIcon="mdi-plus-circle-outline"
      >
        Add Item
      </qvoButton>
    </div>
    <dataTable
      :headers="headers"
      itemKey="_uuid"
      :items="tableItems"
      :class="columns && columns.length < 3 && 'condensed'"
      @pageChange="updatePage"
      @update:sort-by="setSortBy"
      @update:sort-desc="setSortDesc"
    >
      <template
        v-for="column in columns"
        v-slot:[`item.${column}`]="{ item, index }"
      >
        <p
          v-if="getFieldValue('readOnlyFields')"
          :key="`${pagedIndex(index)}-${column}`"
        >
          {{
            items[pagedIndex(index)] &&
            items[pagedIndex(index)][column] &&
            getDisplayAnswer({
              category,
              questionId: item[column]["FieldId"],
              answer: items[pagedIndex(index)][column],
            })
          }}
        </p>
        <configField
          v-else
          :key="`${index}-${column}`"
          :id="ruleId"
          :field="item[column]"
          :value="items[pagedIndex(index)] && items[pagedIndex(index)][column]"
          :category="category"
          :index="pagedIndex(index)"
          :rules="validationRules"
          @setConfigValue="setConfigValue"
          emitSetConfig
          hideLabel
        />
      </template>
      <template v-slot:item.actions="{ index }">
        <v-icon @click="removeItem(pagedIndex(index))"
          >mdi-delete-outline</v-icon
        >
        <v-icon
          v-if="getFieldValue('useModalForAdd')"
          @click="editItem(pagedIndex(index))"
          class="editItem"
        >
          mdi-pencil-outline
        </v-icon>
      </template>
    </dataTable>
  </div>
</template>

<script>
/**
 * @fileoverview Config input field for lists
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { v4 as uuidv4 } from "uuid";
import { mapGetters } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import qvoButton from "@/qux-common/qux22/components/molecules/button";
import dataTable from "@/qux-common/qux22/components/organisms/data-table";
import listAddModal from "./list-add-modal";
import fieldMap from "./field-map.json";
import defaultValueFromType from "@/util/defaultValueFromType";
import getAllAvailableFields from "@/util/getAllAvailableFields";
import getDisplayAnswer from "@/util/getDisplayAnswer";
import getValidationRules from "@/util/getValidationRules";

export default {
  name: "ListField",
  components: {
    qvoButton,
    dataTable,
    listAddModal,
    configField: () => import("../config-field"),
  },
  data() {
    return {
      editIndex: 0,
      editValues: {},
      modalKey: 0,
      page: 1,
      itemsPerPage: 10,
      sortBy: undefined,
      sortDesc: undefined,
    };
  },
  props: {
    availableFieldList: {
      type: Array,
    },
    category: {
      type: String,
    },
    hideLabel: {
      type: Boolean,
    },
    id: {
      type: String,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    rules: {
      type: Array,
    },
    ruleId: {
      type: String,
    },
    value: {
      type: [String, Object, Number, Boolean, Array],
    },
    parentField: {
      type: String,
    },
  },
  methods: {
    /**
     * Sets the value
     */
    setConfigValue({ index, fieldId, value }) {
      const items = this.items;
      items[index][fieldId] = value;
      this.$emit("setConfigValue", items);
    },
    getFieldValue(value, id = this.id) {
      return (
        fieldMap[this.category] &&
        fieldMap[this.category][id] &&
        fieldMap[this.category][id][value]
      );
    },
    addItem(values, isEditingExisting) {
      if (!this.availableFieldList) return;
      let items = this.items;
      if (!isEditingExisting) {
        let newItem = {};
        this.allFields.forEach((field) => {
          const defaultValue =
            field.DefaultValue === undefined
              ? defaultValueFromType(field.Type)
              : field.DefaultValue;
          newItem[field.FieldId] =
            values[field.FieldId] === undefined
              ? defaultValue
              : values[field.FieldId];
        });
        items = [...items, newItem];
      } else {
        items[this.editIndex] = values;
      }
      this.$emit("setConfigValue", items);
    },
    removeItem(index) {
      const items = [...this.items];
      items.splice(index, 1);
      this.$emit("setConfigValue", items);
    },
    editItem(index) {
      this.editIndex = index;
      this.editValues = this.items[index];
    },
    clearEditValues() {
      this.editValues = {};
      this.modalKey = this.modalKey + 1;
    },
    updatePage({ page, itemsPerPage }) {
      this.page = page;
      this.itemsPerPage = itemsPerPage;
    },
    getDisplayAnswer({ category, questionId, answer }) {
      return getDisplayAnswer({ category, questionId, answer });
    },
    pagedIndex(index) {
      return (this.page - 1) * this.itemsPerPage + index;
    },
    setSortBy(sortBy) {
      this.sortBy = sortBy;
    },
    setSortDesc(sortDesc) {
      this.sortDesc = sortDesc;
    },
  },
  computed: {
    items() {
      let configValue = this.value;
      const mergedConfig = cloneDeep(this.mergedConfig);

      if (!configValue.length) {
        let configValue =
          mergedConfig[this.ruleId] && mergedConfig[this.ruleId][this.category];
        if (this.parentField !== undefined) {
          configValue =
            configValue &&
            mergedConfig[this.ruleId][this.category][this.parentField] &&
            mergedConfig[this.ruleId][this.category][this.parentField][this.id];
        } else {
          configValue =
            configValue && mergedConfig[this.ruleId][this.category][this.id];
        }
        if (!configValue) {
          configValue = [];
        }
      }

      //handle sorting
      if (this.sortBy) {
        configValue.sort((a, b) => {
          const firstValue = getDisplayAnswer({
            category: this.category,
            questionId: a[this.sortBy].FieldId,
            answer: a[this.sortBy],
          });
          const secondValue = getDisplayAnswer({
            category: this.category,
            questionId: b[this.sortBy].FieldId,
            answer: b[this.sortBy],
          });

          if (firstValue < secondValue) {
            return this.sortDesc ? 1 : -1;
          }
          if (firstValue > secondValue) {
            return this.sortDesc ? -1 : 1;
          }

          return 0;
        });
      }
      return configValue;
    },
    columns() {
      const columns =
        this.getFieldValue("columnHeaders") !== undefined
          ? this.getFieldValue("columnHeaders").map((column) => column.key)
          : this.availableFieldList &&
            this.availableFieldList.map((field) => field.FieldId);
      return columns;
    },
    headers() {
      const columnHeaders = this.getFieldValue("columnHeaders");
      let generatedFields;

      const columnData = (field) => ({
        text:
          this.getFieldValue("tableColumnName", field.FieldId) !== undefined
            ? this.getFieldValue("tableColumnName", field.FieldId)
            : field.FieldId,
        value: field.FieldId,
      });

      if (columnHeaders) {
        // only display the columns specified in the field data
        generatedFields = columnHeaders.map((column) => {
          const field = this.availableFieldList.find(
            (field) => field.FieldId === column.key
          );
          return columnData(field);
        });
      } else {
        generatedFields =
          (this.availableFieldList &&
            this.availableFieldList.map((field) => columnData(field))) ||
          [];
      }
      return [...generatedFields, { text: "", align: "end", value: "actions" }];
    },
    tableItems() {
      if (!Array.isArray(this.items)) return {};
      return this.items.map((item) => {
        const tableItems = {};
        for (const field in item) {
          const fieldData =
            this.availableFieldList &&
            this.availableFieldList.find((f) => f.FieldId === field);
          tableItems[field] = fieldData;
        }        
        tableItems['_uuid'] = uuidv4();
        return tableItems;
      });
    },
    validationRules() {
      return getValidationRules({
        rules: this.rules,
        inputErrorMessage: this.getFieldValue("inputErrorMessage"),
      });
    },
    allFields() {
      return getAllAvailableFields(this.availableFieldList);
    },
    ...mapGetters({
      mergedConfig: "getMergedConfig",
    }),
  },
};
</script>

<style lang="scss" scoped>
.list {
  margin-bottom: $qvo-spacing-24;
  .add-item-button-wrapper {
    margin: 12px 0 $qvo-spacing-24 0;
  }
  .v-data-table {
    &.condensed {
      max-width: 650px;
    }
    &::v-deep {
      .config-field {
        margin-bottom: 0;
      }
    }
  }
}
.editItem {
  margin-left: 15px;
}
</style>